/**
 * NotificationToast — real-time toast popups for new notifications
 * Polls every 15s, shows new unread notifications as toast popups
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AtSign, Reply, X, MessageSquare } from 'lucide-react';
import api from '../lib/api';

const WE = {
  bgSurface: '#FFFFFF', bgWarm: '#FFF9F0',
  border: '#E8E5E0', textPrimary: '#1C1917', textSecondary: '#57534E', textMuted: '#A8A29E',
  gold: '#C17F0A',
};

const ICON_MAP = {
  mention: AtSign,
  reply: Reply,
};

export default function NotificationToast() {
  const [toasts, setToasts] = useState([]);
  const [ackQueue, setAckQueue] = useState([]);   // popup obavijesti s OBVEZNOM potvrdom
  const lastCheckRef = useRef(null);
  const navigate = useNavigate();

  const checkNewNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      const notifs = data.notifications || [];
      let unread = notifs.filter(n => !n.is_read);

      // 🔔 popup s potvrdom (npr. upitnik za Dejanu): NE nestaje sam, stoji dok se
      // ne klikne "Potvrđujem da sam vidjela" — i vraća se na svakom učitavanju dok je nepročitan
      const acks = unread.filter(n => (n.type || '').endsWith('_ack'));
      if (acks.length) setAckQueue(prev => {
        const known = new Set(prev.map(a => a.id));
        return [...prev, ...acks.filter(a => !known.has(a.id))];
      });
      unread = unread.filter(n => !(n.type || '').endsWith('_ack'));

      if (lastCheckRef.current === null) {
        // First load — just record IDs, don't show toasts
        lastCheckRef.current = new Set(unread.map(n => n.id));
        return;
      }

      // Find truly new notifications (not seen before)
      const newOnes = unread.filter(n => !lastCheckRef.current.has(n.id));
      if (newOnes.length > 0) {
        // Show max 3 toasts at a time
        const toShow = newOnes.slice(0, 3).map(n => ({ ...n, toastId: n.id + '_' + Date.now() }));
        setToasts(prev => [...prev, ...toShow]);
        // Auto-dismiss after 5s
        toShow.forEach(t => {
          setTimeout(() => {
            setToasts(prev => prev.filter(p => p.toastId !== t.toastId));
          }, 5000);
        });
      }

      // Update known IDs
      lastCheckRef.current = new Set(unread.map(n => n.id));
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    checkNewNotifications();
    const interval = setInterval(checkNewNotifications, 15000);
    return () => clearInterval(interval);
  }, [checkNewNotifications]);

  const dismiss = (toastId) => {
    setToasts(prev => prev.filter(t => t.toastId !== toastId));
  };

  const handleClick = async (toast) => {
    try { await api.put('/notifications/' + toast.id + '/read'); } catch (e) {}
    dismiss(toast.toastId);
    if (toast.company_name) {
      navigate('/leads/' + encodeURIComponent(toast.company_name));
    }
  };

  const confirmAck = async (n) => {
    try { await api.put('/notifications/' + n.id + '/read'); } catch (e) { return; }
    setAckQueue(prev => prev.filter(a => a.id !== n.id));
  };

  const ack = ackQueue[0];

  return (
    <>
    {/* ═══ POPUP S OBVEZNOM POTVRDOM ═══ */}
    {ack && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(20,15,5,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          style={{ background: '#FFFFFF', borderRadius: '16px', maxWidth: '440px', width: '100%', padding: '26px 26px 22px',
                   boxShadow: '0 28px 90px rgba(0,0,0,0.4)', border: `2px solid ${WE.gold}`, textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>📋</div>
          <div style={{ fontSize: '17px', fontWeight: 800, color: WE.textPrimary, marginBottom: '6px' }}>{ack.title || 'Nova obavijest'}</div>
          <div style={{ fontSize: '14px', color: WE.textSecondary, marginBottom: '18px', lineHeight: 1.5 }}>{ack.body}</div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {ack.company_name && (
              <button onClick={() => { confirmAck(ack); navigate('/leads/' + encodeURIComponent(ack.company_name)); }}
                style={{ padding: '10px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
                         border: `1px solid ${WE.border}`, background: '#FFF', color: WE.textPrimary }}>
                Otvori profil firme
              </button>
            )}
            <button onClick={() => confirmAck(ack)}
              style={{ padding: '10px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer',
                       border: 'none', background: WE.gold, color: '#FFF' }}>
              ✓ Potvrđujem da sam vidjela
            </button>
          </div>
          {ackQueue.length > 1 && <div style={{ marginTop: '10px', fontSize: '11px', color: WE.textMuted }}>+ još {ackQueue.length - 1} obavijesti čeka potvrdu</div>}
        </motion.div>
      </div>
    )}
    <div style={{ position: 'fixed', bottom: '80px', right: '24px', zIndex: 9999, display: 'flex', flexDirection: 'column-reverse', gap: '8px' }}>
      <AnimatePresence>
        {toasts.map(toast => {
          const Icon = ICON_MAP[toast.type] || MessageSquare;
          return (
            <motion.div
              key={toast.toastId}
              initial={{ opacity: 0, x: 100, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 100, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              onClick={() => handleClick(toast)}
              style={{
                width: '320px', padding: '12px 16px', cursor: 'pointer',
                background: WE.bgSurface, borderRadius: '12px', border: '1px solid ' + WE.border,
                boxShadow: '0 8px 32px rgba(120,100,60,0.2), 0 2px 8px rgba(0,0,0,0.06)',
                display: 'flex', gap: '10px', alignItems: 'flex-start',
              }}
            >
              <div style={{
                width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                background: toast.type === 'mention' ? '#FEF3C7' : WE.bgWarm,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={15} style={{ color: WE.gold }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: WE.textPrimary, marginBottom: '2px' }}>
                  {toast.title || 'Nova obavijest'}
                </div>
                <div style={{ fontSize: '11px', color: WE.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {toast.body || ''}
                </div>
                {toast.created_by && (
                  <div style={{ fontSize: '10px', color: WE.textMuted, marginTop: '2px' }}>
                    od {toast.created_by.split('@')[0]}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); dismiss(toast.toastId); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: WE.textMuted, padding: '2px', flexShrink: 0 }}
              >
                <X size={14} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
    </>
  );
}
